import { useEffect } from "react";

/**
 * Locks background scrolling while a modal is open, without the page jumping to
 * the top.
 *
 * The naive approach — `document.documentElement.style.overflow = "hidden"` —
 * makes the browser drop the scroll offset of the root scroller, so the page
 * snaps to the top behind a translucent backdrop. Instead we pin the body at
 * its current offset with `position: fixed`, then restore the exact scroll
 * position on unlock. All touched inline styles are saved and restored so the
 * layout's own body styles (width / max-width / margin) survive.
 */
export function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;

    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";

    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;

      // Unpinning the body leaves the document at offset 0, so this line is
      // what puts the reader back where they were. It has to be INSTANT:
      // globals.css sets `html { scroll-behavior: smooth }`, which a plain
      // scrollTo obeys — so closing the modal animated a visible scroll from
      // the top of the page back down to the form. Force `auto` for the one
      // call, then hand the smooth behaviour back to the rest of the site.
      const root = document.documentElement;
      const previousScrollBehavior = root.style.scrollBehavior;

      root.style.scrollBehavior = "auto";
      window.scrollTo(0, scrollY);
      root.style.scrollBehavior = previousScrollBehavior;
    };
  }, [locked]);
}
